import classnames from "classnames"
import React, { useMemo, useState } from "react"

import type { ThemeBottomSectionImageItem, ThemeBottomSectionState } from "@/models/ITrackingPageOptions"
import { ThemeLayoutEnum } from "@/models/ITrackingPageOptions"
import {
  LeftLayout,
  RightLayout,
} from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout"
import { BackdropThemeLayoutProvider } from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/ThemeLayoutLeftTabPanels/BackdropThemeLayoutContext"
import { ShowcaseThemeLayoutProvider } from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/ThemeLayoutLeftTabPanels/ShowcaseThemeLayoutContext"
import type { ThemeLayoutBackdropEditorDraft } from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/ThemeLayoutLeftTabPanels/themeLayoutBackdropEditor"
import type { ThemeLayoutShowcaseContentDraft } from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/ThemeLayoutLeftTabPanels/themeLayoutShowcaseDraft"
import { isAdmin, isShopifyEmbedded } from "@/utils/appBridge"

import styles from "./index.module.scss"

export interface ThemeLayoutCustomizeEditorProps {
  variant: ThemeLayoutEnum
  showcaseDraft: ThemeLayoutShowcaseContentDraft
  setShowcaseDraft: React.Dispatch<React.SetStateAction<ThemeLayoutShowcaseContentDraft>>
  backdropDraft: ThemeLayoutBackdropEditorDraft
  setBackdropDraft: React.Dispatch<React.SetStateAction<ThemeLayoutBackdropEditorDraft>>
}

/**
 * App Window 内主题定制主区域：沿用 Email Edit 的左右分栏骨架（左编辑 Tabs + 右预览分段切换）。
 */
const ThemeLayoutCustomizeEditor = ({
  variant,
  showcaseDraft,
  setShowcaseDraft,
  backdropDraft,
  setBackdropDraft,
}: ThemeLayoutCustomizeEditorProps) => {
  const isStandaloneContainer = !isShopifyEmbedded() || window.isFromAdmin

  // 各 variant 的弹窗预览 overlay：弹窗打开期间使用，关闭后置 null，preview 回退到 store 数据
  const [backdropBottomSectionPopupDraft, setBackdropBottomSectionPopupDraft] =
    useState<ThemeBottomSectionState | null>(null)
  const [showcaseBottomSectionPopupDraft, setShowcaseBottomSectionPopupDraft] =
    useState<ThemeBottomSectionState | null>(null)
  const [showcaseTopSectionImagesPopupDraft, setShowcaseTopSectionImagesPopupDraft] =
    useState<ThemeBottomSectionImageItem[] | null>(null)

  const showcaseLayoutValue = useMemo(
    () => ({
      draft: showcaseDraft,
      setDraft: setShowcaseDraft,
      bottomSectionPopupDraft: showcaseBottomSectionPopupDraft,
      setBottomSectionPopupDraft: setShowcaseBottomSectionPopupDraft,
      topSectionImagesPopupDraft: showcaseTopSectionImagesPopupDraft,
      setTopSectionImagesPopupDraft: setShowcaseTopSectionImagesPopupDraft,
    }),
    [showcaseDraft, showcaseBottomSectionPopupDraft, showcaseTopSectionImagesPopupDraft],
  )

  const backdropLayoutValue = useMemo(
    () => ({
      draft: backdropDraft,
      setDraft: setBackdropDraft,
      bottomSectionPopupDraft: backdropBottomSectionPopupDraft,
      setBottomSectionPopupDraft: setBackdropBottomSectionPopupDraft,
    }),
    [backdropDraft, backdropBottomSectionPopupDraft],
  )

  const layoutColumns = (
    <div className={classnames(styles.layout, isStandaloneContainer && styles.containerStandalone)}>
      <div className={styles.leftContent}>
        <LeftLayout variant={variant} />
      </div>
      <div className={styles.rightContent}>
        <RightLayout variant={variant} />
      </div>
    </div>
  )

  return (
    <div
      className={classnames(styles.container, isStandaloneContainer && styles.containerStandalone)}
    >
      {variant === ThemeLayoutEnum.SHOWCASE ? (
        <ShowcaseThemeLayoutProvider value={showcaseLayoutValue}>
          {layoutColumns}
        </ShowcaseThemeLayoutProvider>
      ) : (
        <BackdropThemeLayoutProvider value={backdropLayoutValue}>
          {layoutColumns}
        </BackdropThemeLayoutProvider>
      )}
    </div>
  )
}

export default ThemeLayoutCustomizeEditor
